import { useTranslation } from 'react-i18next';
import { X, Camera, Pin, PinOff } from 'lucide-react';
import type { EnrichedToothImage } from '@/hooks/useToothImages';
import { buildCssFilter } from '@/hooks/useToothImages';
import { useDentalOverlay } from './DentalOverlayContext';

interface ImagePreviewSidebarProps {
  image: EnrichedToothImage;
  patientId: string;
  onClose: () => void;
}

/**
 * 360px-wide sidebar showing the currently selected image meta + quick actions.
 */
export function ImagePreviewSidebar({ image, onClose }: ImagePreviewSidebarProps) {
  const { t } = useTranslation();
  const { pinnedImage, setPinnedImage, setOpacity } = useDentalOverlay();
  const isPinned = pinnedImage?.id === image.id;
  const cssFilter = buildCssFilter(image.adjustments ?? {});

  const handlePinToggle = () => {
    if (isPinned) {
      setPinnedImage(null);
    } else {
      setPinnedImage(image);
      setOpacity(0.5);
    }
  };

  return (
    <div className="flex w-[360px] flex-shrink-0 flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="h-4 w-4 text-indigo-400" />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {t(`dental.imaging.kinds.${image.image_kind}`)}
          </span>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Thumbnail */}
      {image.signedUrl && (
        <div className="overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800">
          <img
            src={image.signedUrl}
            alt={image.document_original_filename ?? ''}
            style={{ filter: cssFilter }}
            className="h-48 w-full object-contain"
          />
        </div>
      )}

      {/* Meta */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
          <span>Dato</span>
          <span className="text-slate-700 dark:text-slate-300">
            {new Date(image.acquired_at).toLocaleDateString('da-DK', {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </span>
        </div>

        {image.tooth_fdis.length > 0 && (
          <div className="flex items-start justify-between">
            <span className="text-slate-500 dark:text-slate-400">Tænder</span>
            <div className="flex flex-wrap justify-end gap-1">
              {image.tooth_fdis.map((fdi) => (
                <span
                  key={fdi}
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                >
                  {fdi}
                </span>
              ))}
            </div>
          </div>
        )}

        {image.notes && (
          <div className="rounded-lg bg-slate-50 p-2 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {image.notes}
          </div>
        )}

        {image.document_size_bytes && (
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
            <span>Størrelse</span>
            <span>{(image.document_size_bytes / 1024 / 1024).toFixed(1)} MB</span>
          </div>
        )}
      </div>

      {/* Pin overlay button */}
      <button
        onClick={handlePinToggle}
        className={`flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${
          isPinned
            ? 'border-indigo-300 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
        }`}
      >
        {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
        {isPinned
          ? t('dental.imaging.gallery.unpinOverlay')
          : t('dental.imaging.gallery.pinOverlay')}
      </button>
    </div>
  );
}
